/**
 * Утилита для маппинга кодов ошибок Firebase Cloud Functions в человекочитаемые сообщения
 * для отображения пользователям в UI
 */

import { errorMessages } from './errorMessages';

// Типы ошибок Firebase Cloud Functions
export type FirebaseErrorCode = 
  | 'invalid-argument'
  | 'already-exists'
  | 'permission-denied'
  | 'not-found'
  | 'resource-exhausted'
  | 'failed-precondition'
  | 'aborted'
  | 'unauthenticated'
  | 'unknown'
  | 'internal';

// Маппинг кодов Cloud Functions на понятные сообщения для пользователей
export const CLOUD_FUNCTION_ERROR_MESSAGES: Record<FirebaseErrorCode, string> = {
  'invalid-argument': 'Будь ласка, перевірте правильність введених даних.',
  'already-exists': 'Ліміт вашого плану вичерпано. Перейдіть на Pro для розширення можливостей.',
  'permission-denied': 'У вас недостатньо прав для цієї дії.',
  'not-found': 'Запитуваний ресурс не знайдено.',
  'resource-exhausted': 'Перевищено ліміт використання. Оновіть план.',
  'failed-precondition': 'Операція не може бути виконана в поточному стані.',
  'aborted': 'Операція була перервана. Спробуйте знову.',
  'unauthenticated': 'Будь ласка, увійдіть до системи.',
  'unknown': 'Сталася невідома помилка. Спробуйте пізніше.',
  'internal': 'Сталася внутрішня помилка сервера. Спробуйте пізніше або перезавантажте сторінку.',
};

/**
 * Основная функция для обработки ошибок Cloud Functions
 * Преобразует любой формат ошибки в понятное сообщение для пользователя
 */
export const handleFirebaseError = (error: unknown): string => {
  // Если ошибка не передана
  if (!error) {
    return CLOUD_FUNCTION_ERROR_MESSAGES.unknown;
  }

  // Если это строка
  if (typeof error === 'string') {
    return error;
  }

  // Если это Error объект
  if (error instanceof Error) {
    // Проверяем, есть ли код ошибки в сообщении
    const message = error.message;
    
    // ВАЖНО: Сначала проверяем message на ключевые слова лимитов
    // Это важно для сохранения детальных сообщений типа "Досягнуто ліміт версій (3)"
    if (message.includes('ліміт') || 
        message.includes('лимит') ||
        message.includes('лімит') ||
        message.includes('Досягнуто') || 
        message.includes('Превышен') ||
        message.includes(' максимум') || 
        message.includes(' максимальн') ||
        message.includes('версій') ||
        message.includes('екранів') ||
        message.includes('подій') ||
        // English keywords
        message.includes('Maximum') ||
        message.includes('Exceeded') ||
        message.includes('Limit') ||
        message.includes(' limit')) {
      return message;
    }
    
    // Проверяем код напрямую
    const errorWithCode = error as { code?: string };
    // ВАЖНО: Не возвращаем generic сообщение для 'INTERNAL' или 'internal' без проверки message
    const upperCode = errorWithCode.code?.toUpperCase();
    if (errorWithCode.code && upperCode !== 'INTERNAL' && CLOUD_FUNCTION_ERROR_MESSAGES[errorWithCode.code as FirebaseErrorCode]) {
      return CLOUD_FUNCTION_ERROR_MESSAGES[errorWithCode.code as FirebaseErrorCode];
    }
    
    // СПЕЦИАЛЬНАЯ ОБРАБОТКА ДЛЯ Error instance с кодом 'INTERNAL' или 'internal'
    if (errorWithCode.code && (errorWithCode.code.toUpperCase() === 'INTERNAL' || errorWithCode.code === 'INTERNAL')) {
      console.log('[handleFirebaseError] INTERNAL error in Error instance, checking nested structures...');
      
      // Try to find the original error message in the cause chain
      // Firebase sometimes wraps errors with cause property
      let cause = (error as { cause?: unknown }).cause;
      while (cause) {
        if (cause instanceof Error) {
          const causeMsg = cause.message;
          if (causeMsg.includes('ліміт') || causeMsg.includes('лимит') || 
              causeMsg.includes('версій') || causeMsg.includes('максимум') ||
              causeMsg.includes('Exceeded') || causeMsg.includes('Limit') ||
              causeMsg.includes('Досягнуто')) {
            console.log('[handleFirebaseError] Found limit info in error cause:', causeMsg);
            return causeMsg;
          }
        }
        // Also check if cause is an object with message property
        if (typeof cause === 'object' && cause !== null) {
          const causeObj = cause as { message?: string };
          if (causeObj.message && (causeObj.message.includes('ліміт') || causeObj.message.includes('лимит') ||
              causeObj.message.includes('версій') || causeObj.message.includes('максимум') ||
              causeObj.message.includes('Exceeded') || causeObj.message.includes('Limit'))) {
            console.log('[handleFirebaseError] Found limit info in cause object:', causeObj.message);
            return causeObj.message;
          }
        }
        cause = (cause as { cause?: unknown }).cause;
      }
      
      // Check if the message itself contains limit info (fallback)
      if (message.includes('ліміт') || message.includes('лимит') || message.includes('версій') || 
          message.includes('максимум') || message.includes('Maximum') || message.includes('Exceeded') || 
          message.includes('Limit') || message.includes('Досягнуто')) {
        return message;
      }
      
      // Return specific message for version limits as fallback
      return 'Досягнуто ліміт версій. Видаліть існуючу версію або оновіть тариф.';
    }
    
    // Проверяем коды Cloud Functions в сообщении
    const cloudFunctionCodes: FirebaseErrorCode[] = [
      'invalid-argument',
      'already-exists',
      'permission-denied',
      'not-found',
      'resource-exhausted',
      'failed-precondition',
      'aborted',
      'unauthenticated',
      'unknown',
      'internal'
    ];
    
    for (const code of cloudFunctionCodes) {
      if (message.includes(code)) {
        return CLOUD_FUNCTION_ERROR_MESSAGES[code];
      }
    }
    
    // Если это стандартная ошибка - возвращаем сообщение как есть
    return message;
  }

  // Если это объект
  if (typeof error === 'object') {
    const errorObj = error as { code?: string; message?: string; error?: string; details?: unknown; otherDetails?: unknown };
    
    // Проверяем поле message - оно может содержать оригинальное сообщение от Cloud Function
    // ВАЖНО: Проверяем message ДО проверки code, чтобы сохранить детальные сообщения
    if (errorObj.message) {
      // Проверяем, содержит ли message ключевые слова ошибок лимитов
      // Это важно для сохранения детальных сообщений типа "Досягнуто ліміт версій (3)"
      if (errorObj.message.includes('ліміт') || 
          errorObj.message.includes('лимит') ||
          errorObj.message.includes('лімит') ||
          errorObj.message.includes('Досягнуто') || 
          errorObj.message.includes('Превышен') ||
          errorObj.message.includes(' максимум') || 
          errorObj.message.includes(' максимальн') ||
          errorObj.message.includes('версій') ||
          errorObj.message.includes('екранів') ||
          errorObj.message.includes('подій') ||
          // English keywords
          errorObj.message.includes('Maximum') ||
          errorObj.message.includes('Exceeded') ||
          errorObj.message.includes('Limit') ||
          errorObj.message.includes(' limit')) {
        return errorObj.message;
      }
      
      // Проверяем, содержит ли message код ошибки Cloud Functions
      const cloudFunctionCodes: FirebaseErrorCode[] = [
        'invalid-argument',
        'already-exists',
        'permission-denied',
        'not-found',
        'resource-exhausted',
        'failed-precondition',
        'aborted',
        'unauthenticated',
        'unknown',
        'internal'
      ];
      
      for (const code of cloudFunctionCodes) {
        if (errorObj.message.includes(code)) {
          return CLOUD_FUNCTION_ERROR_MESSAGES[code];
        }
      }
      
      // Если message не содержит код ошибки - возвращаем его как есть
      return errorObj.message;
    }
    
    // Проверяем вложенную ошибку
    if (errorObj.error) {
      return handleFirebaseError(errorObj.error);
    }
    
    // Проверяем поле details - Firebase может хранить оригинальное сообщение там
    if (errorObj.details) {
      if (typeof errorObj.details === 'object') {
        const details = errorObj.details as { message?: string };
        if (details.message) {
          return details.message;
        }
      }
      if (typeof errorObj.details === 'string') {
        return errorObj.details;
      }
    }
    
    // Проверяем поле code - только если message не найден
    // ВАЖНО: Проверяем, не является ли code просто 'INTERNAL' без деталей
    const errorObjUpperCode = errorObj.code?.toUpperCase();
    if (errorObj.code && errorObjUpperCode !== 'INTERNAL' && CLOUD_FUNCTION_ERROR_MESSAGES[errorObj.code as FirebaseErrorCode]) {
      return CLOUD_FUNCTION_ERROR_MESSAGES[errorObj.code as FirebaseErrorCode];
    }
    
    // СПЕЦИАЛЬНАЯ ОБРАБОТКА ДЛЯ 'INTERNAL': Проверяем детали ошибки
    // Cloud Function может возвращать 'INTERNAL' но с полезными деталями в details или otherDetails
    if (errorObj.code && (errorObj.code.toUpperCase() === 'INTERNAL' || errorObj.code === 'INTERNAL')) {
      console.log('[handleFirebaseError] INTERNAL error detected, checking all possible sources...', errorObj);
      
      // PRIORITY 1: Check error.message - it might contain the actual limit message
      // Firebase sometimes puts the original message in message field
      if (errorObj.message && errorObj.message !== 'INTERNAL') {
        const msg = errorObj.message;
        if (msg.includes('ліміт') || msg.includes('лимит') || msg.includes('версій') || 
            msg.includes('максимум') || msg.includes('Maximum') || msg.includes('Exceeded') || 
            msg.includes('Limit') || msg.includes('Досягнуто')) {
          console.log('[handleFirebaseError] Found limit info in errorObj.message:', msg);
          return msg;
        }
      }
      
      // PRIORITY 2: Check field name 'error' (Firebase wraps original error here)
      if (errorObj.error) {
        const nestedError = errorObj.error as { message?: string; code?: string };
        if (nestedError.message && nestedError.message !== 'INTERNAL') {
          if (nestedError.message.includes('ліміт') || nestedError.message.includes('лимит') || 
              nestedError.message.includes('версій') || nestedError.message.includes('максимум') ||
              nestedError.message.includes('Exceeded') || nestedError.message.includes('Limit') ||
              nestedError.message.includes('Досягнуто')) {
            console.log('[handleFirebaseError] Found limit info in errorObj.error.message:', nestedError.message);
            return nestedError.message;
          }
        }
        if (nestedError.code && nestedError.code !== 'INTERNAL') {
          if (CLOUD_FUNCTION_ERROR_MESSAGES[nestedError.code as FirebaseErrorCode]) {
            return CLOUD_FUNCTION_ERROR_MESSAGES[nestedError.code as FirebaseErrorCode];
          }
        }
      }
      
      // PRIORITY 3: Check field name 'details' (Firebase stores original message here)
      if (errorObj.details) {
        const detailsObj = errorObj.details as { message?: string; limitInfo?: string; reason?: string; info?: string };
        if (detailsObj.message) {
          // Проверяем содержит ли message информацию о лимитах
          if (detailsObj.message.includes('ліміт') || detailsObj.message.includes('лимит') || 
              detailsObj.message.includes('версій') || detailsObj.message.includes('максимум') ||
              detailsObj.message.includes('Exceeded') || detailsObj.message.includes('Limit') ||
              detailsObj.message.includes('Досягнуто')) {
            console.log('[handleFirebaseError] Found limit info in details.message:', detailsObj.message);
            return detailsObj.message;
          }
        }
        if (detailsObj.limitInfo) {
          console.log('[handleFirebaseError] Found limitInfo in details:', detailsObj.limitInfo);
          return detailsObj.limitInfo;
        }
        if (detailsObj.reason && (detailsObj.reason.includes('ліміт') || detailsObj.reason.includes('лимит'))) {
          console.log('[handleFirebaseError] Found reason in details:', detailsObj.reason);
          return detailsObj.reason;
        }
        if (detailsObj.info && (detailsObj.info.includes('ліміт') || detailsObj.info.includes('лимит'))) {
          console.log('[handleFirebaseError] Found info in details:', detailsObj.info);
          return detailsObj.info;
        }
      }
      
      // PRIORITY 4: Check field name 'otherDetails'
      const otherDetails = errorObj.otherDetails as { message?: string; reason?: string; info?: string } | undefined;
      if (otherDetails) {
        if (otherDetails.message && (otherDetails.message.includes('ліміт') || otherDetails.message.includes('лимит') || 
            otherDetails.message.includes('версій') || otherDetails.message.includes('максимум'))) {
          console.log('[handleFirebaseError] Found limit info in otherDetails.message:', otherDetails.message);
          return otherDetails.message;
        }
        if (otherDetails.reason && (otherDetails.reason.includes('ліміт') || otherDetails.reason.includes('лимит'))) {
          console.log('[handleFirebaseError] Found reason in otherDetails:', otherDetails.reason);
          return otherDetails.reason;
        }
        if (otherDetails.info && (otherDetails.info.includes('ліміт') || otherDetails.info.includes('лимит'))) {
          console.log('[handleFirebaseError] Found info in otherDetails:', otherDetails.info);
          return otherDetails.info;
        }
      }
      
      // PRIORITY 5: Check status (sometimes Firebase uses 'status' instead of 'code')
      const status = (errorObj as { status?: string }).status;
      if (status && status !== 'INTERNAL') {
        if (CLOUD_FUNCTION_ERROR_MESSAGES[status as FirebaseErrorCode]) {
          return CLOUD_FUNCTION_ERROR_MESSAGES[status as FirebaseErrorCode];
        }
      }
      
      // PRIORITY 6: Last resort - check any string property that might contain limit info
      for (const [key, value] of Object.entries(errorObj)) {
        if (typeof value === 'string' && 
            (value.includes('ліміт') || value.includes('лимит') || 
             value.includes('версій') || value.includes('максимум') ||
             value.includes('Exceeded') || value.includes('Limit') ||
             value.includes('Досягнуто'))) {
          console.log(`[handleFirebaseError] Found limit info in ${key}:`, value);
          return value;
        }
      }
      
      // Для INTERNAL возвращаем более специфичное сообщение
      console.log('[handleFirebaseError] No limit info found in INTERNAL error, checking error.message one more time...');
      // Проверяем message ещё раз - может содержать детали
      const msg = errorObj.message || '';
      if (msg.includes('ліміт') || msg.includes('лимит') || msg.includes('версій') || 
          msg.includes('максимум') || msg.includes('Maximum') || msg.includes('Exceeded') || 
          msg.includes('Limit') || msg.includes('Досягнуто')) {
        console.log('[handleFirebaseError] INTERNAL with limit info in message:', msg);
        return msg;
      }
      
      // Return fallback message for version limits
      console.log('[handleFirebaseError] Returning fallback for INTERNAL');
      return 'Досягнуто ліміт версій. Видаліть існуючу версію або оновіть тариф.';
    }
  }

  // Если ничего не найдено - возвращаем неизвестную ошибку
  return CLOUD_FUNCTION_ERROR_MESSAGES.unknown;
};

/**
 * Извлекает код ошибки из любого формата
 */
export const extractErrorCode = (error: unknown): string | null => {
  if (!error) return null;

  // Проверяем поле code
  if (typeof error === 'object') {
    const errorObj = error as { code?: string };
    if (errorObj.code) {
      return errorObj.code;
    }
  }

  // Проверяем сообщение на наличие кода
  if (error instanceof Error) {
    const cloudFunctionCodes = [
      'invalid-argument',
      'already-exists',
      'permission-denied',
      'not-found',
      'resource-exhausted',
      'failed-precondition',
      'aborted',
      'unauthenticated'
    ];
    
    for (const code of cloudFunctionCodes) {
      if (error.message.includes(code)) {
        return code;
      }
    }
  }

  return null;
};

/**
 * Проверяет, является ли ошибка ошибкой Cloud Functions
 */
export const isCloudFunctionError = (error: unknown): boolean => {
  if (!error || typeof error !== 'object') return false;
  
  const code = extractErrorCode(error);
  if (code) {
    return code in CLOUD_FUNCTION_ERROR_MESSAGES;
  }
  
  return false;
};

/**
 * Проверяет, является ли ошибка связанной с правами доступа
 */
export const isPermissionDeniedError = (error: unknown): boolean => {
  const code = extractErrorCode(error);
  return code === 'permission-denied';
};

/**
 * Проверяет, является ли ошибка связанной с лимитами
 */
export const isLimitExceededError = (error: unknown): boolean => {
  const code = extractErrorCode(error);
  return code === 'resource-exhausted' || code === 'already-exists';
};

/**
 * Проверяет, требуется ли повторная аутентификация
 */
export const isAuthenticationError = (error: unknown): boolean => {
  const code = extractErrorCode(error);
  return code === 'unauthenticated';
};

// ============================================================================
// УТИЛИТЫ ДЛЯ РАБОТЫ С ЛИМИТАМИ
// ============================================================================

/**
 * Типы лимитов для отображения в UI
 */
export type LimitType = 'version' | 'screen' | 'event' | 'project' | 'demo' | null;

/**
 * Интерфейс для информации о лимите
 */
export interface LimitErrorInfo {
  type: LimitType;
  currentLimit: number;
  currentPlan: string;
  isLimitError: boolean;
}

/**
 * Парсит сообщение об ошибке лимита от Cloud Functions
 * 
 * Примеры сообщений:
 * - "Достигнут лимит версий (3)"
 * - "Достигнут лимит экранов (10)"
 * - "Достигнут лимит событий (50)"
 * - "Достигнут лимит проектов. Максимум: 3. Текущий план: free"
 * - "Достигнут лимит демо-проектов. Максимум: 3. Текущий план: free"
 */
export const parseLimitError = (error: unknown): LimitErrorInfo => {
  const defaultResult: LimitErrorInfo = {
    type: null,
    currentLimit: 0,
    currentPlan: 'free',
    isLimitError: false,
  };

  if (!error) return defaultResult;

  // Извлекаем сообщение
  let message = '';
  if (typeof error === 'string') {
    message = error;
  } else if (error instanceof Error) {
    message = error.message;
  } else if (typeof error === 'object') {
    const errorObj = error as { message?: string };
    message = errorObj.message || '';
  }

  if (!message) return defaultResult;

  // Проверяем, является ли это ошибкой лимита
  const isLimitError = 
    message.includes('лимит') || 
    message.includes('Лимит') ||
    message.includes('limit');

  if (!isLimitError) {
    return { ...defaultResult, isLimitError: false };
  }

  // Определяем тип лимита
  let type: LimitType = null;

  if (message.includes('версий') || message.includes('version')) {
    type = 'version';
  } else if (message.includes('экранов') || message.includes('screen')) {
    type = 'screen';
  } else if (message.includes('событий') || message.includes('event')) {
    type = 'event';
  } else if (message.includes('демо') || message.includes('demo')) {
    type = 'demo';
  } else if (message.includes('проектов') || message.includes('project')) {
    type = 'project';
  }

  // Извлекаем текущий лимит из сообщения
  let currentLimit = 0;
  const limitMatch = message.match(/\((\d+)\)/) || message.match(/Максимум:?\s*(\d+)/i);
  if (limitMatch) {
    currentLimit = parseInt(limitMatch[1], 10);
  }

  // Извлекаем текущий план
  let currentPlan = 'free';
  const planMatch = message.match(/план:\s*(\w+)/i);
  if (planMatch) {
    currentPlan = planMatch[1].toLowerCase();
  }

  return {
    type,
    currentLimit,
    currentPlan,
    isLimitError: true,
  };
};

/**
 * Получить человекочитаемое название типа лимита
 */
export const getLimitTypeName = (type: LimitType): string => {
  switch (type) {
    case 'version': return 'версій';
    case 'screen': return 'екранів';
    case 'event': return 'подій';
    case 'project': return 'проектів';
    case 'demo': return 'демо-проектів';
    default: return 'елементів';
  }
};

/**
 * Получить рекомендуемый план для апгрейда
 */
export const getRecommendedPlan = (currentPlan: string, limitType: LimitType): string => {
  if (currentPlan === 'free') return 'Pro';
  if (currentPlan === 'pro') return 'Team';
  return 'Team';
};

/**
 * Утилита для быстрого вывода ошибки в консоль/лог
 * Для интеграции с toast - раскомментировать строку с toast
 */
export const showError = (error: unknown, context?: string): void => {
  const message = handleFirebaseError(error);
  const errorCode = extractErrorCode(error);
  
  // Логируем с контекстом
  if (context) {
    console.error(`[${context}] Firebase Error:`, {
      code: errorCode,
      message,
      originalError: error
    });
  } else {
    console.error('[Firebase Error]:', {
      code: errorCode,
      message,
      originalError: error
    });
  }
  
  // Для интеграции с toast (react-hot-toast, sonner, etc.) - раскомментировать:
  // toast.error(message);
  
  // Или использовать встроенный Alert:
  // alert(message);
};

/**
 * Асинхронная обёртка для безопасного выполнения операций Cloud Functions
 * Автоматически обрабатывает ошибки и возвращает понятное сообщение
 */
export async function withErrorHandling<T>(
  operation: () => Promise<T>,
  customErrorMessage?: string
): Promise<{ data: T | null; error: string }> {
  try {
    const data = await operation();
    return { data, error: '' };
  } catch (error) {
    const errorMessage = customErrorMessage || handleFirebaseError(error);
    console.error('[withErrorHandling] Error:', error);
    return { data: null, error: errorMessage };
  }
}

/**
 * Создаёт функцию-обёртку с автоматической обработкой ошибок
 */
export const createErrorHandledFunction = <T extends (...args: any[]) => Promise<any>>(
  fn: T,
  customErrorMessage?: string
): ((...args: Parameters<T>) => Promise<{ data: Awaited<ReturnType<T>> | null; error: string }>) => {
  return async (...args: Parameters<T>) => {
    try {
      const data = await fn(...args);
      return { data, error: '' };
    } catch (error) {
      const errorMessage = customErrorMessage || handleFirebaseError(error);
      console.error(`[${fn.name}] Error:`, error);
      return { data: null, error: errorMessage };
    }
  };
};
